import jsPDF from 'jspdf';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { EntitiesContext } from '../../../App';
import { logoUrl } from '../../../constants/logo';
import { useGetOrder } from '../../../hooks/useOrder';
import { useGetSaleInventory } from '../../../hooks/useSale';
import { useGetAccountAddress } from '../../../hooks/useAccount';
import Loader from '../../Loader';

export default function Reports() {
  const entities = useContext(EntitiesContext);

  const [filter, setFilter] = useState(null);
  const [data, setData] = useState();
  const [groups, setGroups] = useState();
  const sale = entities.state.get('sale').find(s => s.status === 'open');
  const orders = entities.state.get('order');
  const inventory = entities.state.get('inventory');
  const [hour, setHour] = useState(null);

  const { isLoading } = useGetOrder();
  const vendorInventory = useGetSaleInventory(sale, 'vendor');
  const address = useGetAccountAddress();

  const vendors = entities.state.get('account')
    .filter(a => a.role === 'vendor');

  useEffect(() => {
    if (filter) {
      address.mutate(entities.state.get('account').get(parseInt(filter)));
    }
  }, [filter]);

  useEffect(() => {
    if (!filter || inventory.count() > 0) {
      setData(setupData(inventory));
    }
  }, [filter, inventory]);

  const hasPaidOrder = inventory => {
    return orders.get(inventory.order_id)?.status === 'paid';
  }

  const currentInventory = i => {
    return ((vendors.get(i.account_id) && i.order_id) && orders?.find(o => o.id === i.order_id && o.status === 'paid')) && i.sale_id === sale.id;
  }

  const setupData = d => {
    return d
      .filter(i => currentInventory(i))
      .filter(i => hasPaidOrder(i))
      .filter(i => filter ? i.account_id === parseInt(filter) : true)
      .map(i => ({
        name: i.name,
        price: i.price,
        order_id: i.order_id,
        createdAt: orders.find(o => i.order_id === o.id).createtAt
      }));
  }

  const filterInventory = () => {
    setGroups(null);
    setData(setupData(inventory));
  }

  const groupInventory = () => {
    const prices = {};

    data.forEach(i => {
      if (prices[i.price]) prices[i.price]++;
      if (!prices[i.price]) prices[i.price] = 1;
    })

    setGroups(prices);
    return prices;
  }

  const generatePrintReport = () => {
    const prices = groupInventory();
    const doc = new jsPDF();
    let y = 20;

    const totalItems = data
      .filter(i => i.order_id)
      .count();

    const totalValue = data
      .filter(i => i.order_id)
      .reduce((arr, curr) => arr + curr.price, 0)
      .toFixed(2);

    // Add the logo image (replace with your Base64 string or URL)

    doc.addImage(logoUrl, 'PNG', 180, 2, 30, 30); // x, y, width, height

    const vendor = vendors.find(v => v.id === parseInt(filter));
    const vendorName = vendor ? vendor.vendor_name : "All Vendors";
    const address = entities.state.get('address').find(a => a.account_id === vendor.id);

    // Title and Vendor Name
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(`Vendor Report for `, 10, y);
    const vendorNameX = doc.getTextDimensions(`Vendor Report for `).w + 10;
    doc.setFont("helvetica", "italic");
    doc.text(vendorName, vendorNameX, y);
    y += 3; // Move down a little for spacing after the title

    // Underline the title
    doc.setDrawColor(0);
    doc.line(10, y, 180, y); // Draw the line immediately after the title
    y += 8; // Move down for the next section

    // Add these details to the PDF
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Vendor: ${vendorName}`, 10, y);
    doc.text(`Total Items: ${totalItems}`, 10, y += 6);
    doc.text(`Total Value: $${new Intl.NumberFormat().format(totalValue)}`, 10, y += 6);
    doc.text(`PTA Percentage: $${new Intl.NumberFormat().format((totalValue * 0.20).toFixed(2))}`, 10, y += 6);
    doc.text(`Take Home: $${new Intl.NumberFormat().format((totalValue * 0.80).toFixed(2))}`, 10, y += 6);
    doc.text(`Generated at: ${DateTime.local().toLocaleString(DateTime.DATETIME_FULL)}`, 10, y += 6);


    // Add extra space before the thank you message
    y += 10; // This adds extra space after the "Generated at" line

    // Multiline text for thank you message
    const thankYouText = `Thank you for being a part of the Waitsfield Elementary School PTA's ${DateTime.local().toFormat("yyyy")} Ski & Skate Sale. Your check will be mailed to you roughly 1 week after the sale. For any questions regarding the sale, or issues with your report please email waitsfieldelementarypta@gmail.com`;

    // Define the maximum width for the text
    const maxWidth = 190; // Adjust this value according to your layout

    // Split the text into an array of lines that fit within the specified width
    const textLines = doc.splitTextToSize(thankYouText, maxWidth);

    // Loop through the lines and add each to the document
    const lineHeight = 7; // Set the desired line height here
    textLines.forEach((line, index) => {
      doc.text(line, 10, y + (index * lineHeight)); // Adjusted line height
    });

    // Update y to the last line position
    y += textLines.length * lineHeight; // Adjusted line height

    // Add spacing after the thank you message if needed
    y += 6; // Optional: Add more space after the thank you message if necessary

    // Grouped Data Header
    if (prices) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Grouped by Price:", 10, y);
      y += 8;

      // Table Header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Price", 10, y);
      doc.text("Total", 100, y);
      y += 5;

      // Underline the header
      doc.setDrawColor(200);
      doc.line(10, y, 190, y); // Draw a line under the header
      y += 5;

      // Reset font for data rows
      doc.setFont("helvetica", "normal");

      Object.keys(prices).forEach((price) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }

        // Formatting the price and total with borders
        doc.setDrawColor(200);
        doc.rect(10, y - 5, 90, 10); // Price cell border
        doc.rect(100, y - 5, 90, 10); // Total cell border

        // Adding data
        doc.setFillColor(240); // Light gray background for the rows
        doc.rect(10, y - 5, 90, 10, 'F'); // Price cell background
        doc.rect(100, y - 5, 90, 10, 'F'); // Total cell background

        doc.setTextColor(0); // Reset text color to black
        doc.text(`$${price}`, 10 + 5, y);
        doc.text(prices[price].toString(), 100 + 5, y);
        y += 10;
      });

      // Save the PDF
      const timestamp = DateTime.local().toFormat("yyyy-MM-dd HH-mm-ss");
      doc.save(`[REPORT] ${vendorName} generated at ${timestamp}.pdf`);

    }
  };

  if (!data || (isLoading || vendorInventory.isLoading)) return <Loader />

  return (
    <div>
      <div className="title">
        Vendor Reports
      </div>

      <div className="columns">
        <div className="column is-narrow">
          <div className="select">
            <select name="vendors" onChange={e => setFilter(e.target.value)}>
              <option value="">--</option>
              {vendors.valueSeq().map(v => (
                <option key={v.id} value={v.id}>
                  {v.vendor_name} ({entities.state.get('inventory').filter(i => i.account_id === v.id && currentInventory(i)).count()})
                </option>
              ))}
            </select>
          </div>
        </div>
        {filter && <div className="column is-narrow">
          <button className="button is-info" onClick={groupInventory}>Group By Price</button>
        </div>}
        {filter && <div className="column">
          <button className="button is-dark" onClick={generatePrintReport}>Generate Print Report</button>
        </div>}
      </div>

      <nav className="level">
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">Total Items</p>
            <p className="title">
              {data
                .filter(i => hasPaidOrder(i))
                .count()}
            </p>
          </div>
        </div>
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">Total Value</p>
            <p className="title">
              ${(data
                .filter(i => hasPaidOrder(i))
                .reduce((arr, curr) => arr + curr.price, 0))
                .toFixed(2)}
            </p>
          </div>
        </div>
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">PTA Take</p>
            <p className="title">
              ${(data
                .filter(i => hasPaidOrder(i))
                .reduce((arr, curr) => arr + curr.price, 0) * 0.20)
                .toFixed(2)}
            </p>
          </div>
        </div>
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">Sold</p>
            <p className="title">
              {data
                .filter(i => hasPaidOrder(i))
                .count()} :
              ${data
                .filter(i => hasPaidOrder(i))
                .reduce((arr, curr) => arr + curr.price, 0)}
            </p>
          </div>
        </div>
      </nav>

      {groups && <table className="table is-fullwidth">
        <thead>
          <tr>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groups).map((price) => (
            <tr key={price}>
              <td>${price}</td>
              <td>{groups[price]}</td>
            </tr>
          ))}
        </tbody>
      </table>}

      {(!groups && (data.count() > 0 && filter)) && <table className="table is-fullwidth">
        <thead>
          <tr>
            <th>Name</th>
            <th>Price</th>
            <th>Purchased</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data
            .sortBy(d => d && d.createdAt)
            .filter(d => {
              if (!hour) return d;
              const order = orders.find(o => o.id === d.order_id);
              const dt = DateTime.fromISO(order.createdAt);
              return dt.hour === hour;
            })
            .reverse()
            .valueSeq()
            .map(i => <tr key={i.id}>
              <td>{i.name}</td>
              <td>${i.price}</td>
              <td>{DateTime.fromISO(orders.find(o => o.id === i.order_id).createdAt).toLocaleString(DateTime.DATETIME_FULL)}</td>
              <td>
                <a onClick={() => setHour(DateTime.fromISO(orders.find(o => o.id === i.order_id).createdAt).hour)}>{DateTime.fromISO(orders.find(o => o.id === i.order_id).createdAt).toFormat('h a')}</a>
                {hour && <span className="ml-3"><a onClick={() => setHour(null)}>x</a></span>}
              </td>
            </tr>)}
        </tbody>
      </table>}
    </div>
  );
}
